import { useEffect, useState } from 'react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { TooltipProvider } from '@/components/ui/tooltip';
import { GlobalTooltip } from '@/components/ui/global-tooltip';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/error-boundary';
import { DEFAULT_TEMPLATE_ID } from '@/features/editor/data/templates';
import { routeTree } from './routeTree.gen';
import { mapCenemaToSuperEdit } from './utils/cenema-mapper';
import { injectCtaIntoTimeline, removeCtaFromTimeline, upsertCtaIntoTimeline } from './utils/cta-insertion';
import { toast } from 'sonner';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  const [isAppLoading, setIsAppLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('integration') === 'cenema-studio' || params.get('viewmode') === 'true';
  });

  // Prevent default browser zoom application-wide
  useEffect(() => {
    const wheelListenerOptions: AddEventListenerOptions = { passive: false, capture: true };
    const keyListenerOptions: AddEventListenerOptions = { capture: true };

    const preventBrowserZoom = (e: WheelEvent) => {
      // Prevent browser zoom when Ctrl/Cmd is held
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    const preventKeyboardZoom = (e: KeyboardEvent) => {
      // Prevent browser zoom shortcuts: Ctrl+=/+/-, Ctrl+0
      // Only preventDefault (blocks browser zoom), event still propagates to react-hotkeys-hook
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_' || e.key === '0') {
          e.preventDefault();
          // DO NOT call stopPropagation() - we want react-hotkeys-hook to still receive this
        }
      }
    };

    // Add listeners at capture phase to intercept before browser handles them
    document.addEventListener('wheel', preventBrowserZoom, wheelListenerOptions);
    document.addEventListener('keydown', preventKeyboardZoom, keyListenerOptions);

    return () => {
      document.removeEventListener('wheel', preventBrowserZoom, wheelListenerOptions);
      document.removeEventListener('keydown', preventKeyboardZoom, keyListenerOptions);
    };
  }, []);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Security: In production, check event.origin here

      if (event.data?.type === 'LOAD_CENEMA_PROJECT' || event.data?.type === 'LOAD_PROJECT') {
        const payload = event.data.payload || event.data.data;
        console.group('SuperEdit: Bridge Load');
        console.log('Payload Received:', payload);
        const pendingProjectId = payload?.id || payload?.projectId;

        try {
          if (pendingProjectId) {
            sessionStorage.setItem('superedit:pending-default-template-project-id', pendingProjectId);
          }
        } catch {
          // noop
        }

        try {
          const getPayloadRatio = (input: any): string => {
            return (
              input?.ratio ||
              input?.aspect_ratio ||
              input?.data?.ratio ||
              input?.data?.aspect_ratio ||
              input?.input_data?.ratio ||
              input?.input_data?.aspect_ratio ||
              '16:9'
            ).toString().toLowerCase();
          };

          const isVerticalPayload = (input: any): boolean => {
            const ratio = getPayloadRatio(input);
            if (
              ratio.includes('9:16') ||
              ratio.includes('1:1') ||
              ratio.includes('shorts') ||
              ratio.includes('vertical') ||
              ratio.includes('square')
            ) {
              return true;
            }

            const metadataWidth = Number(
              input?.proData?.metadata?.width ??
              input?.metadata?.width ??
              input?.data?.metadata?.width ??
              input?.input_data?.metadata?.width
            );
            const metadataHeight = Number(
              input?.proData?.metadata?.height ??
              input?.metadata?.height ??
              input?.data?.metadata?.height ??
              input?.input_data?.metadata?.height
            );

            if (
              Number.isFinite(metadataWidth) &&
              Number.isFinite(metadataHeight) &&
              metadataWidth > 0 &&
              metadataHeight > 0
            ) {
              return metadataHeight > metadataWidth;
            }

            return false;
          };

          const getPayloadScenes = (input: any): any[] => {
            const candidates = [
              input?.script_timeline,
              input?.data?.script_timeline,
              input?.input_data?.script_timeline,
              input?.scenes,
              input?.data?.scenes,
              input?.input_data?.scenes,
            ];

            const found = candidates.find((value) => Array.isArray(value) && value.length > 0);
            return Array.isArray(found) ? found : [];
          };

          const readFourCC = (view: DataView, offset: number) =>
            String.fromCharCode(
              view.getUint8(offset),
              view.getUint8(offset + 1),
              view.getUint8(offset + 2),
              view.getUint8(offset + 3)
            );

          const getExactWavDurationFromArrayBuffer = (buffer: ArrayBuffer): number | null => {
            if (buffer.byteLength < 44) return null;

            const view = new DataView(buffer);
            if (readFourCC(view, 0) !== 'RIFF' || readFourCC(view, 8) !== 'WAVE') {
              return null;
            }

            let offset = 12;
            let sampleRate = 0;
            let byteRate = 0;
            let blockAlign = 0;
            let bitsPerSample = 0;
            let dataSize = 0;

            while (offset + 8 <= view.byteLength) {
              const chunkId = readFourCC(view, offset);
              const chunkSize = view.getUint32(offset + 4, true);
              const chunkDataOffset = offset + 8;

              if (chunkId === 'fmt ' && chunkDataOffset + 16 <= view.byteLength) {
                sampleRate = view.getUint32(chunkDataOffset + 4, true);
                byteRate = view.getUint32(chunkDataOffset + 8, true);
                blockAlign = view.getUint16(chunkDataOffset + 12, true);
                bitsPerSample = view.getUint16(chunkDataOffset + 14, true);
              } else if (chunkId === 'data') {
                dataSize = chunkSize;
                break;
              }

              offset = chunkDataOffset + chunkSize + (chunkSize % 2);
            }

            if (dataSize <= 0) return null;
            if (byteRate > 0) return dataSize / byteRate;
            if (sampleRate > 0 && blockAlign > 0) return dataSize / (sampleRate * blockAlign);
            if (sampleRate > 0 && bitsPerSample > 0) return dataSize / (sampleRate * (bitsPerSample / 8));
            return null;
          };

          const getExactAudioDurationFromUrl = async (url: string): Promise<number | null> => {
            if (!url) return null;

            try {
              const response = await fetch(url);
              if (!response.ok) return null;
              const arrayBuffer = await response.arrayBuffer();
              const wavDuration = getExactWavDurationFromArrayBuffer(arrayBuffer);
              if (Number.isFinite(wavDuration) && wavDuration! > 0) {
                return wavDuration!;
              }
            } catch {
              // fallback below
            }

            return await new Promise<number | null>((resolve) => {
              const audio = new Audio();
              let cleanedUp = false;
              const cleanup = () => {
                if (cleanedUp) return;
                cleanedUp = true;
                audio.removeAttribute('src');
                audio.load();
              };

              audio.preload = 'metadata';
              audio.addEventListener('loadedmetadata', () => {
                const duration = audio.duration;
                cleanup();
                resolve(Number.isFinite(duration) && duration > 0 ? duration : null);
              });
              audio.addEventListener('error', () => {
                cleanup();
                resolve(null);
              });
              audio.src = url;
              audio.load();
            });
          };

          const hydratePayloadAudioDurations = async (input: any) => {
            const scenes = getPayloadScenes(input);
            if (scenes.length === 0) return;

            await Promise.all(scenes.map(async (scene: any, index: number) => {
              const audioUrl = scene?.source_audio_url || scene?.audio_url || scene?.generatedAudioUrl || scene?.audioReferenceUrl;
              if (!audioUrl || typeof audioUrl !== 'string') return;

              const measuredDuration = await getExactAudioDurationFromUrl(audioUrl);
              if (!Number.isFinite(measuredDuration) || measuredDuration! <= 0) return;

              console.log('[SuperEdit][AudioHydrate]', {
                sceneIndex: index + 1,
                audioUrl,
                previousAudioDuration: scene?.audio_duration,
                measuredDuration,
                speechStartMs: scene?.speech_start_ms ?? scene?.speechStartMs ?? scene?.audioStartMs,
                speechEndMs: scene?.speech_end_ms ?? scene?.speechEndMs ?? scene?.audioEndMs,
                captionSegments: Array.isArray(scene?.caption_segments) ? scene.caption_segments : scene?.captionSegments,
                wordTimestamps: Array.isArray(scene?.word_timestamps)
                  ? scene.word_timestamps
                  : Array.isArray(scene?.speechToText?.words)
                    ? scene.speechToText.words
                    : undefined,
              });
              scene.audio_duration = measuredDuration;

              // Also propagate to payload.scenes at the same index (mapper uses scenes array)
              if (Array.isArray(input?.scenes) && input.scenes[index] && !input.scenes[index].audio_duration) {
                input.scenes[index].audio_duration = measuredDuration;
              }
            }));
          };

          await hydratePayloadAudioDurations(payload);

          const hasSceneSpeechTiming = (scene: any) => {
            const speechStart = Number(scene?.speech_start_ms ?? scene?.speechStartMs ?? scene?.audioStartMs);
            const speechEnd = Number(scene?.speech_end_ms ?? scene?.speechEndMs ?? scene?.audioEndMs);
            if (Number.isFinite(speechStart) && Number.isFinite(speechEnd) && speechEnd > speechStart) {
              return true;
            }

            if (Array.isArray(scene?.caption_segments) && scene.caption_segments.length > 0) {
              return true;
            }

            if (Array.isArray(scene?.captionSegments) && scene.captionSegments.length > 0) {
              return true;
            }

            const wordTimestamps = Array.isArray(scene?.word_timestamps)
              ? scene.word_timestamps
              : Array.isArray(scene?.speechToText?.words)
                ? scene.speechToText.words
                : [];

            return wordTimestamps.length > 0;
          };

          const getSceneDurationFrames = (scene: any, fps: number) => {
            const audioDuration = Number(scene?.audio_duration);
            const duration = Number(scene?.duration_sec ?? scene?.duration);
            const audioFrames = Number.isFinite(audioDuration) && audioDuration > 0
              ? Math.max(1, Math.round(audioDuration * fps))
              : 0;
            const durationFrames = Number.isFinite(duration) && duration > 0
              ? Math.max(1, Math.round(duration * fps))
              : 0;

            if (audioFrames > 0) return audioFrames;
            return Math.max(durationFrames, fps * 2);
          };

          const normalizeTimelineToPayloadScenes = (
            rawItems: any[],
            rawPayload: any,
            fps: number,
          ) => {
            const scenes = getPayloadScenes(rawPayload);
            if (scenes.length === 0 || rawItems.length === 0) return rawItems;

            const desiredWindows = scenes.map((scene, index) => {
              const durationInFrames = getSceneDurationFrames(scene, fps);
              return {
                index,
                from: 0,
                durationInFrames,
                audioDurationInFrames: durationInFrames,
              };
            });

            let cursor = 0;
            desiredWindows.forEach((window) => {
              window.from = cursor;
              cursor += window.durationInFrames;
            });

            const sceneItems = rawItems
              .filter((item: any) => item?.label?.startsWith('Scene '))
              .sort((a: any, b: any) => a.from - b.from);

            if (sceneItems.length === 0) return rawItems;

            const oldWindows = sceneItems.map((item: any, index: number) => {
              const nextItem = sceneItems[index + 1];
              const fallbackEnd = item.from + Math.max(1, item.durationInFrames || 1);
              return {
                index,
                from: item.from,
                to: nextItem ? nextItem.from : fallbackEnd,
              };
            });

            const sceneCount = Math.min(oldWindows.length, desiredWindows.length);
            const normalizedItems = rawItems.map((item: any) => ({ ...item }));

            for (let index = 0; index < sceneCount; index += 1) {
              const oldWindow = oldWindows[index];
              const desiredWindow = desiredWindows[index];
              const expectedSceneId = `scene-${index + 1}`;

              normalizedItems.forEach((item: any) => {
                const originalFrom = item.from;
                const originalDuration = Math.max(1, item.durationInFrames || 1);
                const itemSceneId = String(item?.sceneId || '');
                const itemSceneIndex = Number.isFinite(Number(item?.sceneIndex)) ? Number(item.sceneIndex) : null;
                const matchesSceneIdentity =
                  itemSceneId === expectedSceneId ||
                  itemSceneIndex === index;
                const hasExplicitSceneIdentity = Boolean(itemSceneId) || itemSceneIndex !== null;
                const isSceneWindowItem =
                  (
                    matchesSceneIdentity ||
                    (originalFrom >= oldWindow.from && originalFrom < oldWindow.to)
                  ) &&
                  (!hasExplicitSceneIdentity || matchesSceneIdentity) &&
                  !String(item.trackId || '').startsWith('background');

                if (!isSceneWindowItem) return;

                const relativeStart = originalFrom - oldWindow.from;
                const maxAllowedDuration = Math.max(1, desiredWindow.durationInFrames - Math.min(relativeStart, desiredWindow.durationInFrames - 1));
                item.from = desiredWindow.from + Math.min(relativeStart, desiredWindow.durationInFrames - 1);

                if (item.type === 'audio') {
                  const desiredAudioDuration = Math.max(
                    1,
                    Math.min(desiredWindow.audioDurationInFrames, maxAllowedDuration)
                  );
                  const hasExplicitTrimWindow =
                    Number.isFinite(Number(item.sourceEnd)) ||
                    Number(item.sourceStart || 0) > 0 ||
                    Number(item.trimStart || 0) > 0;
                  item.durationInFrames = desiredAudioDuration;
                  item.sourceStart = Number.isFinite(Number(item.sourceStart)) ? item.sourceStart : 0;
                  item.sourceDuration = Number.isFinite(Number(item.sourceDuration))
                    ? Math.max(desiredAudioDuration, Number(item.sourceDuration))
                    : item.sourceStart + desiredAudioDuration;
                  if (hasExplicitTrimWindow) {
                    item.sourceEnd = item.sourceStart + desiredAudioDuration;
                  } else {
                    delete item.sourceEnd;
                  }
                  item.trimStart = Number.isFinite(Number(item.trimStart)) ? item.trimStart : 0;
                  item.trimEnd = Number.isFinite(Number(item.trimEnd)) ? item.trimEnd : 0;
                  return;
                }

                if (item.label?.startsWith('Scene ')) {
                  item.durationInFrames = desiredWindow.durationInFrames;
                  return;
                }

                item.durationInFrames = Math.max(1, Math.min(originalDuration, maxAllowedDuration));
              });
            }

            return normalizedItems;
          };

          const extractSceneWindowsFromItems = (rawItems: any[]) => {
            if (!Array.isArray(rawItems) || rawItems.length === 0) return [];

            const preferredItems = rawItems
              .filter((item: any) => item?.label?.startsWith('Scene ') && String(item?.trackId || '') === 'video-track')
              .sort((a: any, b: any) => Number(a?.from || 0) - Number(b?.from || 0));

            const fallbackItems = rawItems
              .filter((item: any) => item?.label?.startsWith('Scene '))
              .sort((a: any, b: any) => Number(a?.from || 0) - Number(b?.from || 0));

            const sceneItems = preferredItems.length > 0 ? preferredItems : fallbackItems;

            return sceneItems.map((item: any, index: number) => ({
              index,
              from: Math.max(0, Number(item?.from || 0)),
              durationInFrames: Math.max(1, Number(item?.durationInFrames || 1)),
            }));
          };

          const buildExpectedSceneWindows = (rawPayload: any, fps: number) => {
            const scenes = getPayloadScenes(rawPayload);
            if (scenes.length === 0) return [];

            let cursor = 0;
            return scenes.map((scene: any, index: number) => {
              const durationInFrames = getSceneDurationFrames(scene, fps);
              const window = {
                index,
                from: cursor,
                durationInFrames,
              };
              cursor += durationInFrames;
              return window;
            });
          };

          const proDataMatchesPayloadScenes = (rawPayload: any, _fps: number) => {
            // 저장된 proData에 timeline.items가 있으면 무조건 신뢰
            // 씬 수/타이밍 비교는 템플릿 트랙(Title1/Title2/Background) 때문에 false positive 발생
            const items = rawPayload?.proData?.timeline?.items;
            if (!Array.isArray(items) || items.length === 0) return false;
            return true;
          };

          const replaceTimelineTracks = (
            baseTracks: any[],
            baseItems: any[],
            replacementTracks: any[],
            replacementItems: any[],
            trackIds: string[]
          ) => {
            const trackIdSet = new Set(trackIds);
            const nextItems = [
              ...baseItems.filter((item: any) => !trackIdSet.has(String(item?.trackId || ''))),
              ...replacementItems.filter((item: any) => trackIdSet.has(String(item?.trackId || ''))),
            ];

            const replacementTrackMap = new Map(
              replacementTracks
                .filter((track: any) => trackIdSet.has(String(track?.id || '')))
                .map((track: any) => [track.id, track])
            );

            const nextTracks = baseTracks.map((track: any) => {
              if (!trackIdSet.has(String(track?.id || ''))) return track;
              const replacement = replacementTrackMap.get(track.id);
              if (!replacement) {
                return {
                  ...track,
                  items: [],
                };
              }
              return replacement;
            });

            replacementTracks.forEach((track: any) => {
              if (!trackIdSet.has(String(track?.id || ''))) return;
              if (!nextTracks.some((existing: any) => existing.id === track.id)) {
                nextTracks.push(track);
              }
            });

            return {
              tracks: nextTracks,
              items: nextItems,
            };
          };

          console.log('--- DEEP PAYLOAD INSPECTION ---');
          console.log('Payload Type:', typeof payload);
          if (payload) {
            Object.keys(payload).forEach(key => {
              console.log(`Key: ${key}, Type: ${typeof payload[key]}, Value:`, payload[key]);
            });
            // Specific check for nested data string
            if (typeof payload.data === 'string') {
              try {
                console.log('Payload.data is a string! Attempting to parse...');
                payload.data = JSON.parse(payload.data);
              } catch (e) {
                console.error('Failed to parse payload.data string');
              }
            }
            if (typeof payload.output_data === 'string') {
              try {
                payload.output_data = JSON.parse(payload.output_data);
              } catch (e) { }
            }
          }

          // Data extraction and mapping (initial baseline)
          const { useItemsStore: importItemsStore } = await import('@/features/timeline/stores/items-store');
          const currentItemsForMapping = importItemsStore.getState().items;
          const verticalPayload = isVerticalPayload(payload);
          let { tracks, items, assets, keyframes = [] } = mapCenemaToSuperEdit(payload, 30, verticalPayload, currentItemsForMapping);
          let hasProDataOverrides = false;
          const hasValidProDataTimeline = proDataMatchesPayloadScenes(payload, 30);

          // 🆕 Prioritize restoring from Superedit's previously saved timeline state
          if (payload?.proData?.timeline?.items && payload?.proData?.timeline?.items.length > 0 && hasValidProDataTimeline) {
            console.log('SuperEdit: 💾 Restoring FULL custom timeline from proData. Payload:', {
              itemCount: payload.proData.timeline.items?.length,
              trackCount: payload.proData.timeline.tracks?.length,
              keyframeCount: payload.proData.timeline.keyframes?.length,
              assetCount: payload.proData.assets?.length
            });

            if (payload.proData.timeline.items) {
              const sfxItems = payload.proData.timeline.items.filter((it: any) => it.trackId?.toLowerCase().includes('sfx') || it.trackId === 'audio-track');
              console.log('SuperEdit: 🔍 SFX items to restore:', sfxItems.length, sfxItems);
            }

            tracks = payload.proData.timeline.tracks;
            items = payload.proData.timeline.items;
            keyframes = payload.proData.timeline.keyframes || [];
            hasProDataOverrides = true;

            // 🆕 Relink expired URLs: Match old items to fresh assets by fileName
            // This prevents "Missing Media References" when security tokens in URLs change
            if (assets && assets.length > 0 && items && items.length > 0) {
              console.log('SuperEdit: 🔗 Attempting to relink potentially expired URLs...');

              const freshAssetMap = new Map(); // fileName -> freshUrl
              assets.forEach(a => { if (a.fileName) freshAssetMap.set(a.fileName, a.id); });

              const oldAssetMap = new Map(); // oldUrl -> fileName
              if (Array.isArray(payload.proData.assets)) {
                payload.proData.assets.forEach((pa: any) => {
                  const id = pa.id || pa.mediaId;
                  if (id) oldAssetMap.set(id, pa.fileName);
                });
              }

              let relinkCount = 0;
              items.forEach((item: any) => {
                if (item.mediaId && (item.mediaId.startsWith('http') || item.mediaId.includes('://'))) {
                  const fileName = oldAssetMap.get(item.mediaId);
                  const freshUrl = fileName ? freshAssetMap.get(fileName) : null;

                  if (freshUrl && freshUrl !== item.mediaId) {
                    // console.log(`SuperEdit: Relinking clip ${item.label} (${fileName}) to fresh URL`);
                    item.mediaId = freshUrl;
                    if (item.src) item.src = freshUrl;
                    relinkCount++;
                  }
                }
              });

              if (relinkCount > 0) {
                console.info(`SuperEdit: ✅ Successfully relinked ${relinkCount} clips to fresh URLs.`);
              }
            }
          } else if (payload?.proData?.timeline?.items?.length) {
            console.warn('SuperEdit: Ignoring stale proData timeline. Rebuilding from payload scenes.');
          }

          // Emergency Fallback: If itemCount is 0, let's try to hunt for scenes manually here
          if (items.length === 0 && payload && !hasProDataOverrides) {
            console.warn('Emergency: itemCount is 0. Attempting brute-force scene recovery...');
            const hunt = (obj: any): any[] | null => {
              if (!obj || typeof obj !== 'object') return null;
              if (Array.isArray(obj.scenes) && obj.scenes.length > 0) return obj.scenes;
              for (const k in obj) {
                const found = hunt(obj[k]);
                if (found) return found;
              }
              return null;
            };
            const recoveredScenes = hunt(payload);
            if (recoveredScenes) {
              console.info('Brute-force recovered scenes count:', recoveredScenes.length);
              const reMapped = mapCenemaToSuperEdit({ ...payload, scenes: recoveredScenes }, 30, verticalPayload, currentItemsForMapping);
              tracks = reMapped.tracks;
              items = reMapped.items;
              assets = reMapped.assets;
              keyframes = reMapped.keyframes || [];
            }
          }

          // DIAGNOSTIC MODE: If STILL 0, inject a dummy item to prove renderer works
          if (items.length === 0) {
            const payloadKeys = Object.keys(payload || {}).join(', ');
            console.error(`CRITICAL: No scenes found in payload. Keys: ${payloadKeys}`);
            toast.error(`데이터 로드 실패! (Scene 데이터 없음) Keys: ${payloadKeys}`);

            toast.info("렌더링 진단을 위해 테스트 클립을 강제 생성합니다.");
            const dummyId = crypto.randomUUID();
            const dummyItem = {
              id: dummyId,
              type: "text",
              trackId: "caption-track",
              from: 0,
              durationInFrames: 90, // 3s
              label: "DIAGNOSTIC",
              text: "데이터 로드 실패\n(진단용 클립)",
              fontSize: 80,
              fontFamily: "Inter",
              fontWeight: "bold",
              color: "#ff0000",
              textAlign: "center",
              verticalAlign: "middle",
              lineHeight: 1.2,
              transform: {
                x: 0, y: 0, width: 1920, height: 1080, rotation: 0, opacity: 1,
              },
            };

            // Ensure text track exists
            let textTrack = tracks.find(t => t.id === 'caption-track');
            if (!textTrack) {
              textTrack = { id: "caption-track", name: "Caption", order: 2, height: 40, locked: false, visible: true, muted: false, solo: false, items: [] };
              tracks.push(textTrack);
            }

            items.push(dummyItem as any);
            textTrack.items.push(dummyItem as any);
          }

          console.log('Final Mapped Result:', { trackCount: tracks.length, itemCount: items.length, assetCount: assets.length });

          // --- ROBUST ID HUNTING V5 (Prioritize Real IDs) ---
          const urlParams = new URL(window.location.href).searchParams;
          const urlProjectId = urlParams.get('projectId');

          const candidates = [
            payload.projectId,
            payload.project_id,
            payload.data?.projectId,
            payload.data?.project_id,
            urlProjectId,
            payload.id,
            payload.data?.id
          ];

          const projectId = candidates.find(c => c && c !== 'cenema-lab') || candidates.find(c => c) || 'cenema-lab';

          if (projectId === 'cenema-lab') {
            console.warn('SuperEdit Bridge: ⚠️ Using fallback ID "cenema-lab". This will prevent My Page sync.');
          } else {
            console.info('SuperEdit Bridge: ✅ Successfully resolved real Project ID:', projectId);
          }

          // Dynamic imports for storage
          const { getProject, createProject, updateProject } = await import('@/lib/storage/indexeddb');
          const { getMedia, createMedia, updateMedia } = await import('@/lib/storage/indexeddb/media');
          const { associateMediaWithProject } = await import('@/lib/storage/indexeddb/project-media');

          // --- ROBUST TITLE HUNTING --- study
          const huntForTitle = (obj: any, depth = 0): string | null => {
            if (!obj || depth > 6) return null;

            // Priority keys study
            const priorityKeys = ['project_name', 'projectTitle', 'projectName', 'title', 'name', 'subject', 'headline'];
            for (const key of priorityKeys) {
              if (typeof obj[key] === 'string' && obj[key].length > 1 && obj[key] !== 'Cenema Project') {
                return obj[key];
              }
            }

            // Depth Search study
            if (typeof obj === 'object') {
              // Priority sub-objects study
              const subObjKeys = ['data', 'input_data', 'script_data', 'step4', 'payload', 'project', 'info'];
              for (const key of subObjKeys) {
                const found = huntForTitle(obj[key], depth + 1);
                if (found) return found;
              }

              // Check steps array study
              if (Array.isArray(obj.steps)) {
                for (let i = obj.steps.length - 1; i >= 0; i--) {
                  const found = huntForTitle(obj.steps[i], depth + 1);
                  if (found) return found;
                }
              }
            }
            return null;
          };

          const projectTitle = huntForTitle(event.data) || huntForTitle(payload) || 'Cenema Project';
          console.log('SuperEdit: Resolved Project Title:', projectTitle);
          // ----------------------------- study
          const projectThumb = payload?.thumbnail_url || payload?.data?.thumbnail || payload?.thumbnail || '';

          // Calculate duration from items
          const maxFrame = items.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
          const calculatedDuration = Math.max(30, Math.ceil(maxFrame / 30));

          // --- ASPECT RATIO DETECTION ---
          let width = 1920;
          let height = 1080;

          // 1. Try to restore exact saved dimensions from proData
          if (payload?.proData?.metadata?.width && payload?.proData?.metadata?.height) {
            width = payload.proData.metadata.width;
            height = payload.proData.metadata.height;
            console.log(`Detected exact saved aspect ratio from proData: ${width}x${height}`);
          } else {
            // 2. Fallback to parsing text ratio
            const ratioRaw = (
              payload?.data?.ratio ||
              payload?.ratio ||
              payload?.data?.aspect_ratio ||
              payload?.input_data?.aspect_ratio ||
              payload?.aspect_ratio ||
              "16:9"
            ).toString().toLowerCase();

            if (ratioRaw.includes("1:1") || ratioRaw.includes("square")) {
              console.log('Detected 1:1 aspect ratio, mapping to vertical editor canvas (1080x1920)');
              width = 1080;
              height = 1920;
            } else if (ratioRaw.includes("9:16") || ratioRaw.includes("shorts") || ratioRaw.includes("vertical")) {
              console.log('Detected SHORTS aspect ratio (9:16)');
              width = 1080;
              height = 1920;
            } else {
              console.log('Detected LONGFORM aspect ratio (16:9)');
              width = 1920;
              height = 1080;
            }
          }
          // ------------------------------

          const incomingCtaConfig = payload?.ctaConfig || payload?.input_data?.ctaConfig;
          const timelineWithCta = (() => {
            if (!hasProDataOverrides) {
              return injectCtaIntoTimeline({
                tracks,
                items,
                assets,
                ctaConfig: incomingCtaConfig,
                width,
                height,
                fps: 30,
              });
            }

            if (incomingCtaConfig?.enabled) {
              return upsertCtaIntoTimeline({
                tracks,
                items,
                assets,
                ctaConfig: incomingCtaConfig,
                width,
                height,
                fps: 30,
              });
            }

            const cleaned = removeCtaFromTimeline({ tracks, items });
            return {
              tracks: cleaned.tracks,
              items: cleaned.items,
              assets,
            };
          })();

          tracks = timelineWithCta.tracks;
          assets = timelineWithCta.assets;

          const payloadScenes = getPayloadScenes(payload);
          const allowCanonicalSpeechTiming = isVerticalPayload(payload);
          const hasCanonicalTiming = allowCanonicalSpeechTiming && payloadScenes.some((scene: any) => hasSceneSpeechTiming(scene));
          const hasScenePayload = payloadScenes.length > 0;

          if (hasProDataOverrides) {
            items = timelineWithCta.items;
          } else if (hasCanonicalTiming) {
            console.log('SuperEdit: Using canonical speech timing from payload scenes.');
            items = timelineWithCta.items;
          } else {
            if (!allowCanonicalSpeechTiming && payloadScenes.some((scene: any) => hasSceneSpeechTiming(scene))) {
              console.log('SuperEdit: Ignoring canonical speech timing for non-vertical project.');
            }
            if (hasScenePayload) {
              console.log('SuperEdit: Using canonical mapped payload timeline without secondary normalization.');
              items = timelineWithCta.items;
            } else {
              items = normalizeTimelineToPayloadScenes(timelineWithCta.items, payload, 30);
            }
          }

          if (hasProDataOverrides) {
            // proData가 있으면 저장된 타임라인을 그대로 복원 — 트랙 교체 없음
            console.log('SuperEdit: proData override active. Using saved timeline as-is.');
          } else if (hasCanonicalTiming) {
            console.log('SuperEdit: Using canonical payload timing for new project tracks.');
          }

          const finalMaxFrame = items.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
          const finalCalculatedDuration = Math.max(30, Math.ceil(finalMaxFrame / 30));

          const timeline: any = {
            tracks,
            items,
            currentFrame: 0,
            zoomLevel: 0.22,
            keyframes,
          };

          // 🆕 Restore transitions and markers if they exist in proData
          if (hasProDataOverrides && payload?.proData?.timeline?.transitions) {
            console.log('SuperEdit: 💾 Restoring Transitions to timeline object:', payload.proData.timeline.transitions.length);
            timeline.transitions = payload.proData.timeline.transitions;
          }
          if (hasProDataOverrides && payload?.proData?.timeline?.markers) {
            console.log('SuperEdit: 💾 Restoring Markers to timeline object:', payload.proData.timeline.markers.length);
            timeline.markers = payload.proData.timeline.markers;
          }

          const projectData = {
            id: projectId,
            name: projectTitle,
            description: 'Loaded from Cenema',
            createdAt: Date.now(),
            updatedAt: Date.now(),
            duration: finalCalculatedDuration,
            metadata: {
              width,
              height,
              fps: 30,
              backgroundColor: '#000000',
              thumbnail: projectThumb,
            },
            brandProfileId: payload?.brandProfileId || payload?.input_data?.brandProfileId,
            ctaConfig: payload?.ctaConfig || payload?.input_data?.ctaConfig,
            input_data: {
              ...(payload.input_data || payload.data || {}),
              _hostSyncTimestamp: payload?._hostSyncTimestamp || payload?.input_data?._hostSyncTimestamp || Date.now(),
              availableBrandProfiles: payload?.availableBrandProfiles || payload?.input_data?.availableBrandProfiles || [],
              availableCtaPresets: payload?.availableCtaPresets || payload?.input_data?.availableCtaPresets || [],
            },
            timeline,
          };

          const existing = await getProject(projectId);
          if (existing) {
            await updateProject(projectId, projectData);
          } else {
            await createProject(projectData as any);
          }

          // Sync assets
          // 🆕 Prioritize restoring assets from proData if available, otherwise fallback to newly mapped assets
          let mergedAssets = [...(assets || [])];

          if (payload?.proData?.assets && Array.isArray(payload.proData.assets)) {
            // Merge proData assets, avoiding duplicates by ID
            const assetIds = new Set(mergedAssets.map(a => a.id));
            payload.proData.assets.forEach((pa: any) => {
              if (!assetIds.has(pa.id)) {
                mergedAssets.push(pa);
                assetIds.add(pa.id);
              }
            });
          }

          if (mergedAssets && mergedAssets.length > 0) {
            console.log(`[Bridge] Syncing ${mergedAssets.length} assets to IndexedDB...`);
            for (const asset of mergedAssets) {
              const id = asset.id || asset.mediaId;
              if (!id) continue;

              const mediaMetadata = {
                id: id,
                storageType: asset.storageType || 'url',
                fileName: asset.fileName || 'Asset',
                fileSize: asset.fileSize || 0,
                mimeType: asset.mimeType || 'application/octet-stream',
                duration: asset.duration || 0,
                width: asset.width || 0,
                height: asset.height || 0,
                fps: asset.fps || 30,
                codec: asset.codec || 'unknown',
                bitrate: asset.bitrate || 0,
                tags: asset.tags || ['cenema'],
                createdAt: asset.createdAt || Date.now(),
                updatedAt: asset.updatedAt || Date.now(),
              };

              try {
                const existingMedia = await getMedia(id);
                if (existingMedia) {
                  // Update URL and basic metadata, but protect local storage paths
                  if (existingMedia.storageType !== 'handle' && existingMedia.storageType !== 'opfs') {
                    await updateMedia(id, mediaMetadata as any);
                  }
                } else {
                  await createMedia(mediaMetadata as any);
                }

                await associateMediaWithProject(projectId, id);
              } catch (e) {
                // Ignore silent errors for existing records, but log others
                console.error(`Asset ${id} sync error:`, e);
              }
            }
          }

          toast.success('Project Synced!');

          // Check if we are already in the editor for this project
          const currentPath = router.state.location.pathname;
          const isInEditor = currentPath.includes(`/editor/${projectId}`);

          if (isInEditor) {
            console.log('SuperEdit: Already in Editor. Triggering manual reload...');

            // 1. Sync Project Context (Header/Title)
            const { useProjectStore } = await import('@/features/projects/stores/project-store');
            const { useEditorStore } = await import('@/features/editor/stores/editor-store');

            // Force the default template and Templates tab on load to override stale localStorage study
            useEditorStore.getState().setActiveTemplateId(DEFAULT_TEMPLATE_ID);
            useEditorStore.getState().setActiveTab('templates');

            useProjectStore.getState().setCurrentProject({
              id: projectData.id,
              name: projectData.name,
              description: projectData.description,
              duration: projectData.duration,
              metadata: projectData.metadata,
              createdAt: projectData.createdAt,
              updatedAt: projectData.updatedAt,
              brandProfileId: projectData.brandProfileId,
              ctaConfig: projectData.ctaConfig,
              input_data: projectData.input_data,
              output_data: payload.output_data || {},
            });

            // 2. Sync Media Library (Media Panel)
            const { useMediaLibraryStore } = await import('@/features/media-library/stores/media-library-store');
            useMediaLibraryStore.getState().setCurrentProject(projectId);
            await useMediaLibraryStore.getState().loadMediaItems();

            // 3. Sync Timeline (Clips/Tracks)
            const { useTimelineStore } = await import('@/features/timeline/stores/timeline-store');
            await useTimelineStore.getState().loadTimeline(projectId);

            window.dispatchEvent(new CustomEvent('superedit:apply-default-template', {
              detail: { projectId, templateId: DEFAULT_TEMPLATE_ID }
            }));
          } else {
            await router.navigate({
              to: '/editor/$projectId',
              params: { projectId }
            });
          }

          // Stop app loading AFTER navigation or sync
          setIsAppLoading(false);

          // Notify success
          window.parent.postMessage({
            type: 'EDITOR_LOADED',
            payload: { editor: 'superedit', projectName: payload?.project_name }
          }, '*');

        } catch (error) {
          console.error('SuperEdit Bridge Error:', error);
          toast.error('Bridge processing failed');
          setIsAppLoading(false);
        } finally {
          console.groupEnd();
        }
      }

      if (event.data?.type === 'SET_CLOUD_TEMPLATES') {
        const templates = event.data.payload;
        console.log('SuperEdit: Received cloud templates', templates);
        const { useEditorStore } = await import('@/features/editor/stores/editor-store');
        useEditorStore.getState().setCloudTemplates(templates);
      }
    };

    window.addEventListener('message', handleMessage);

    // Handshake: Tell parent we are ready to receive
    window.parent.postMessage({ type: 'EDITOR_READY', data: { editor: 'superedit' } }, '*');
    window.parent.postMessage({ type: 'CENEMA_STUDIO_READY', data: { editor: 'superedit' } }, '*');
    window.parent.postMessage({ type: 'REQUEST_CLOUD_TEMPLATES' }, '*');
    console.log('SuperEdit: Sent Handshake (EDITOR_READY, CENEMA_STUDIO_READY, REQUEST_CLOUD_TEMPLATES)');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // TooltipProvider at app level to prevent re-renders cascading from Editor
  // GlobalTooltip for performant data-tooltip based tooltips
  // Toaster for toast notifications
  // ErrorBoundary for graceful error recovery
  return (
    <ErrorBoundary level="app">
      {isAppLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          color: '#fff',
          fontFamily: 'sans-serif'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mx-auto"></div>
          </div>
        </div>
      )}
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
        <GlobalTooltip />
        <Toaster />
      </TooltipProvider>
    </ErrorBoundary>
  );
}
